<template>
  <div v-if="currentClaim">
    <div class="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
      <ShowPageHeader backLabel="Sinistres" backRoute="/claims" :title="currentClaim.friendlyId">
        <template #badges>
          <FieldSpan
            :class="[statusStyle(currentClaim.status)]"
            translation-prefix="claim.status"
            :value="currentClaim.status"
          />
          <div
            v-if="claimPickupScheduled && currentClaim.pickupAppointmentDate"
            class="flex items-center gap-1.5 rounded-lg p-2 text-xs text-gray-600 bg-blue-100"
          >
            <CalendarIcon class="w-4 h-4 text-blue-500" />
            <span>RDV de reprise le {{ currentClaim.pickupAppointmentDate }}</span>
          </div>
        </template>
      </ShowPageHeader>

      <div v-if="anyActionRequired || true">
        <RequiredAction :claim="currentClaim" />
      </div>
    </div>

    <CompensationValueAlert
      v-if="replacementHandlingType && currentClaim.compensationValue"
      :compensation-value="currentClaim.compensationValue"
      class="mt-4"
    />

    <RequiredActionAlert v-if="anyActionRequired" class="mt-4" />

    <div class="mt-4 flex flex-col lg:flex-row gap-3">
      <div class="flex-1 min-w-0">
        <div class="relative bg-white rounded-2xl shadow-md p-6">
          <h3 class="text-lg font-bold text-gray-900">Déclaration</h3>
          <ClaimDeclaration
            v-if="currentClaim.declarations && currentClaim.declarations.length"
            class="mt-4"
            :declaration="currentClaim.declarations[0]"
          />

          <ClaimProductSummary v-if="claimProduct" :claim-product="claimProduct" class="mt-4" />
        </div>

        <ClaimDeductibleInformation
          v-if="currentClaim.claimDeductible"
          :claim-deductible="currentClaim.claimDeductible"
          class="mt-3"
        />
        <FinalSettlementInformation
          v-if="currentClaim.finalSettlement"
          :final-settlement="currentClaim.finalSettlement"
          class="mt-3"
        />
      </div>

      <div class="lg:w-80 xl:w-96 shrink-0">
        <div class="bg-white rounded-2xl shadow-md p-4">
          <h3 class="text-lg font-bold text-gray-900 mb-4">Historique</h3>
          <ClaimHistoricalChanges :claim="currentClaim" />
        </div>
      </div>
    </div>

    <TabsNavigation v-if="tabs.length > 0" :tabs="tabs" class="my-6" />

    <Quotations v-if="currentTab === 'quotations'" :quotations="claimQuotations" class="mt-6" />

    <Invoices v-if="currentTab === 'invoices'" :invoices="claimInvoices" class="mt-6" />

    <VoucherInformation
      v-if="currentTab === 'voucher' && currentClaim.voucher"
      :voucher="currentClaim.voucher"
      class="mt-6"
    />
  </div>

  <Loading v-else />
</template>

<script>
import ClaimHistoricalChanges from "@/components/claim/details/information/ClaimHistoricalChanges.vue";
import RequiredAction from "@/components/claim/details/action/RequiredAction.vue";
import RequiredActionAlert from "@/components/claim/details/action/RequiredActionAlert.vue";
import { CalendarIcon } from "@heroicons/vue/outline";
import {
  ClaimDeclaration,
  ClaimProductSummary,
  Loading,
  FieldSpan,
  TabsNavigation,
  VoucherInformation,
  CompensationValueAlert,
  ShowPageHeader,
} from "@estaly/ui";
import { mapActions, mapGetters, mapState } from "vuex";
import ClaimDeductibleInformation from "@/components/claim/details/information/ClaimDeductibleInformation.vue";
import FinalSettlementInformation from "@/components/claim/details/information/FinalSettlementInformation.vue";
import Quotations from "@/components/claim/details/quotation/Quotations.vue";
import Invoices from "@/components/claim/details/invoice/Invoices.vue";

export default {
  components: {
    CalendarIcon,
    FieldSpan,
    FinalSettlementInformation,
    VoucherInformation,
    ClaimDeductibleInformation,
    ClaimProductSummary,
    RequiredAction,
    RequiredActionAlert,
    ClaimHistoricalChanges,
    ClaimDeclaration,
    Loading,
    TabsNavigation,
    Quotations,
    Invoices,
    CompensationValueAlert,
    ShowPageHeader,
  },

  computed: {
    ...mapState("claims", ["currentClaim"]),
    ...mapGetters("claims", ["anyActionRequired", "claimPickupScheduled", "statusStyle", "replacementHandlingType"]),
    ...mapGetters("claimQuotations", { claimQuotations: "getIndex" }),
    ...mapGetters("claimInvoices", { claimInvoices: "getIndex" }),

    claimProduct() {
      return this.currentClaim?.claimProduct;
    },

    tabs() {
      const availableTabs = [];

      if (this.claimQuotations.length > 0) {
        availableTabs.push({
          name: "quotations",
          displayName: "Devis",
          query: { tab: "quotations" },
        });
      }

      if (this.claimInvoices.length > 0) {
        availableTabs.push({
          name: "invoices",
          displayName: "Factures",
          query: { tab: "invoices" },
        });
      }

      if (this.currentClaim?.voucher) {
        availableTabs.push({
          name: "voucher",
          displayName: "Avoir",
          query: { tab: "voucher" },
        });
      }

      return availableTabs;
    },

    currentTab() {
      const requestedTab = this.$route.query.tab;
      const availableTabs = this.tabs.map((tab) => tab.name);

      if (availableTabs.length === 0) return null;
      if (requestedTab && availableTabs.includes(requestedTab)) return requestedTab;
      return availableTabs[0];
    },
  },

  watch: {
    "$route.query.tab": {
      immediate: true,
      handler(requestedTab) {
        const availableTabs = this.tabs.map((tab) => tab.name);
        if (availableTabs.length === 0) return;
        if (!requestedTab || !availableTabs.includes(requestedTab)) {
          this.$nextTick(() => {
            this.$router.replace({ query: { ...this.$route.query, tab: availableTabs[0] } });
          });
        }
      },
    },
  },

  async created() {
    await this.fetch(this.$route.params.id);
    if (this.currentClaim?.id) {
      await Promise.all([
        this.fetchClaimQuotations({ claimId: this.currentClaim.id }),
        this.fetchClaimInvoices({ claimId: this.currentClaim.id }),
      ]);
    }
  },

  methods: {
    ...mapActions("claims", ["fetch"]),
    ...mapActions("claimQuotations", { fetchClaimQuotations: "fetchIndex" }),
    ...mapActions("claimInvoices", { fetchClaimInvoices: "fetchIndex" }),
  },
};
</script>
